import { memo } from 'react';
import { IconFile, IconFileText, IconPhoto } from '@tabler/icons-react';

import { ICON_SIZE_SM } from '@/constants/ui';
import { getFileExtension } from '@/utils/fileValidation';

import type { FileTypeIconProps } from './FileTypeIcon.types';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const DOC_EXTENSIONS = ['pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'csv'];

/**
 * FileTypeIcon Component
 *
 * Source-agnostic icon reflecting a file's kind, chosen from its MIME type
 * (preferred) or filename extension (fallback): image → IconPhoto,
 * document/text/pdf → IconFileText, otherwise → IconFile.
 *
 * Shared by AttachmentRow and the task-wizard summary file list.
 */
export const FileTypeIcon = memo(function FileTypeIcon({
  name,
  mimeType,
  size = ICON_SIZE_SM,
}: FileTypeIconProps) {
  if (mimeType) {
    if (mimeType.startsWith('image/')) {
      return <IconPhoto size={size} />;
    }
    if (mimeType === 'application/pdf' || mimeType.includes('text/')) {
      return <IconFileText size={size} />;
    }
  }

  const extension = getFileExtension(name);

  if (IMAGE_EXTENSIONS.includes(extension)) {
    return <IconPhoto size={size} />;
  }
  if (DOC_EXTENSIONS.includes(extension)) {
    return <IconFileText size={size} />;
  }

  return <IconFile size={size} />;
});
